export interface ColorSpot {
  id: number;
  x: number;
  y: number;
  color: string;
}

// K-means clustering types (private)
interface RGB {
  r: number;
  g: number;
  b: number;
}

interface SampledPixel extends RGB {
  x: number;
  y: number;
}

interface Cluster {
  centroid: RGB;
  pixels: SampledPixel[];
}

export const colorDistance = (c1: number[], c2: number[]): number => {
  return Math.sqrt(
    Math.pow(c1[0] - c2[0], 2) +
    Math.pow(c1[1] - c2[1], 2) +
    Math.pow(c1[2] - c2[2], 2)
  );
};

export const getContrastColor = (rgbColor: string): string => {
  const match = rgbColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (!match) return "black";
  const r = parseInt(match[1], 10);
  const g = parseInt(match[2], 10);
  const b = parseInt(match[3], 10);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 128 ? "black" : "white";
};

export const parseRgbColor = (rgbColor: string): number[] | null => {
  const match = rgbColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (!match) return null;
  return [parseInt(match[1], 10), parseInt(match[2], 10), parseInt(match[3], 10)];
};

export const rgbToHex = (rgbColor: string): string => {
  const match = rgbColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (!match) return rgbColor;
  const r = parseInt(match[1], 10);
  const g = parseInt(match[2], 10);
  const b = parseInt(match[3], 10);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`.toUpperCase();
};

// === COLOR CONVERSION FUNCTIONS ===

/**
 * Convert RGB to HSL
 * Returns [h, s, l] where h is 0-360, s and l are 0-100
 */
export const rgbToHsl = (r: number, g: number, b: number): [number, number, number] => {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
};

/**
 * Convert HSL to RGB
 * Expects h in 0-360, s and l in 0-100
 * Returns [r, g, b] in 0-255
 */
export const hslToRgb = (h: number, s: number, l: number): [number, number, number] => {
  h /= 360;
  s /= 100;
  l /= 100;

  let r, g, b;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
};

/**
 * Parse RGB string and convert to HSL
 */
export const rgbStringToHsl = (rgbColor: string): [number, number, number] | null => {
  const rgb = parseRgbColor(rgbColor);
  if (!rgb) return null;
  return rgbToHsl(rgb[0], rgb[1], rgb[2]);
};

/**
 * Convert HSL to RGB string
 */
export const hslToRgbString = (h: number, s: number, l: number): string => {
  const [r, g, b] = hslToRgb(h, s, l);
  return `rgb(${r},${g},${b})`;
};

// === COLOR HARMONY FUNCTIONS ===

export interface ColorHarmony {
  name: string;
  colors: string[];
}

/**
 * Generate complementary color (180° opposite on color wheel)
 */
export const getComplementary = (rgbColor: string): string[] => {
  const hsl = rgbStringToHsl(rgbColor);
  if (!hsl) return [];
  const [h, s, l] = hsl;
  return [hslToRgbString((h + 180) % 360, s, l)];
};

/**
 * Generate analogous colors (±30° on color wheel)
 */
export const getAnalogous = (rgbColor: string): string[] => {
  const hsl = rgbStringToHsl(rgbColor);
  if (!hsl) return [];
  const [h, s, l] = hsl;
  return [
    hslToRgbString((h + 330) % 360, s, l), // -30°
    hslToRgbString((h + 30) % 360, s, l),  // +30°
  ];
};

/**
 * Generate triadic colors (120° apart on color wheel)
 */
export const getTriadic = (rgbColor: string): string[] => {
  const hsl = rgbStringToHsl(rgbColor);
  if (!hsl) return [];
  const [h, s, l] = hsl;
  return [
    hslToRgbString((h + 120) % 360, s, l),
    hslToRgbString((h + 240) % 360, s, l),
  ];
};

/**
 * Generate split-complementary colors (150° and 210° from base)
 */
export const getSplitComplementary = (rgbColor: string): string[] => {
  const hsl = rgbStringToHsl(rgbColor);
  if (!hsl) return [];
  const [h, s, l] = hsl;
  return [
    hslToRgbString((h + 150) % 360, s, l),
    hslToRgbString((h + 210) % 360, s, l),
  ];
};

/**
 * Generate tetradic/square colors (90° apart on color wheel)
 */
export const getTetradic = (rgbColor: string): string[] => {
  const hsl = rgbStringToHsl(rgbColor);
  if (!hsl) return [];
  const [h, s, l] = hsl;
  return [
    hslToRgbString((h + 90) % 360, s, l),
    hslToRgbString((h + 180) % 360, s, l),
    hslToRgbString((h + 270) % 360, s, l),
  ];
};

/**
 * Get all color harmonies for a given color
 */
export const getAllHarmonies = (rgbColor: string): ColorHarmony[] => {
  return [
    { name: 'Complementary', colors: getComplementary(rgbColor) },
    { name: 'Analogous', colors: getAnalogous(rgbColor) },
    { name: 'Triadic', colors: getTriadic(rgbColor) },
    { name: 'Split-Complementary', colors: getSplitComplementary(rgbColor) },
    { name: 'Tetradic', colors: getTetradic(rgbColor) },
  ];
};

export const getPixelColor = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number
): string => {
  const pixelData = ctx.getImageData(Math.round(x), Math.round(y), 1, 1).data;
  return `rgb(${pixelData[0]},${pixelData[1]},${pixelData[2]})`;
};

export const generateRandomPalette = (
  image: HTMLImageElement,
  imageSize: { width: number; height: number },
  startId: number,
  numColors: number = 6,
  minColorDistance: number = 50
): { spots: ColorSpot[]; nextId: number } => {
  const canvas = document.createElement("canvas");
  canvas.width = imageSize.width;
  canvas.height = imageSize.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return { spots: [], nextId: startId };

  ctx.drawImage(image, 0, 0, imageSize.width, imageSize.height);

  const maxAttempts = 500;
  const newSpots: ColorSpot[] = [];
  const selectedColors: number[][] = [];
  let currentId = startId;

  for (let i = 0; i < maxAttempts && newSpots.length < numColors; i++) {
    const x = Math.floor(Math.random() * imageSize.width);
    const y = Math.floor(Math.random() * imageSize.height);
    const pixelData = ctx.getImageData(x, y, 1, 1).data;
    const rgb = [pixelData[0], pixelData[1], pixelData[2]];

    const isDifferent = selectedColors.every(
      (existing) => colorDistance(rgb, existing) >= minColorDistance
    );

    if (isDifferent) {
      selectedColors.push(rgb);
      newSpots.push({
        id: currentId++,
        x,
        y,
        color: `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`,
      });
    }
  }

  return { spots: newSpots, nextId: currentId };
};

// === K-MEANS DOMINANT COLOR EXTRACTION ===

/**
 * Sample pixels from image using grid sampling for performance
 */
const samplePixels = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  targetSamples: number = 5000
): SampledPixel[] => {
  const totalPixels = width * height;
  const step = Math.max(1, Math.floor(Math.sqrt(totalPixels / targetSamples)));

  const samples: SampledPixel[] = [];
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;

  for (let y = 0; y < height; y += step) {
    for (let x = 0; x < width; x += step) {
      const idx = (y * width + x) * 4;
      const r = data[idx];
      const g = data[idx + 1];
      const b = data[idx + 2];
      const a = data[idx + 3];

      // Skip transparent pixels
      if (a < 128) continue;

      samples.push({ r, g, b, x, y });
    }
  }

  return samples;
};

/**
 * Initialize centroids using k-means++ algorithm for better starting positions
 */
const initializeCentroidsKMeansPlusPlus = (
  pixels: SampledPixel[],
  k: number
): RGB[] => {
  const centroids: RGB[] = [];

  // Choose first centroid randomly
  const firstIdx = Math.floor(Math.random() * pixels.length);
  centroids.push({
    r: pixels[firstIdx].r,
    g: pixels[firstIdx].g,
    b: pixels[firstIdx].b
  });

  // Choose remaining centroids with probability proportional to distance squared
  while (centroids.length < k) {
    const distances: number[] = pixels.map(pixel => {
      const minDist = Math.min(
        ...centroids.map(c =>
          colorDistance([pixel.r, pixel.g, pixel.b], [c.r, c.g, c.b])
        )
      );
      return minDist * minDist;
    });

    const totalDist = distances.reduce((sum, d) => sum + d, 0);
    let random = Math.random() * totalDist;

    for (let i = 0; i < pixels.length; i++) {
      random -= distances[i];
      if (random <= 0) {
        centroids.push({
          r: pixels[i].r,
          g: pixels[i].g,
          b: pixels[i].b
        });
        break;
      }
    }
  }

  return centroids;
};

/**
 * Run k-means clustering algorithm
 */
const kMeansClustering = (
  pixels: SampledPixel[],
  k: number,
  maxIterations: number = 20
): Cluster[] => {
  if (pixels.length === 0) return [];

  let centroids = initializeCentroidsKMeansPlusPlus(pixels, k);
  let clusters: Cluster[] = [];

  for (let iteration = 0; iteration < maxIterations; iteration++) {
    // Create empty clusters
    clusters = centroids.map(c => ({ centroid: c, pixels: [] }));

    // Assign each pixel to nearest centroid
    for (const pixel of pixels) {
      let minDist = Infinity;
      let nearestIdx = 0;

      for (let i = 0; i < centroids.length; i++) {
        const dist = colorDistance(
          [pixel.r, pixel.g, pixel.b],
          [centroids[i].r, centroids[i].g, centroids[i].b]
        );
        if (dist < minDist) {
          minDist = dist;
          nearestIdx = i;
        }
      }

      clusters[nearestIdx].pixels.push(pixel);
    }

    // Recalculate centroids
    const newCentroids: RGB[] = clusters.map(cluster => {
      if (cluster.pixels.length === 0) {
        return cluster.centroid;
      }

      const sum = cluster.pixels.reduce(
        (acc, p) => ({ r: acc.r + p.r, g: acc.g + p.g, b: acc.b + p.b }),
        { r: 0, g: 0, b: 0 }
      );

      return {
        r: Math.round(sum.r / cluster.pixels.length),
        g: Math.round(sum.g / cluster.pixels.length),
        b: Math.round(sum.b / cluster.pixels.length),
      };
    });

    // Check for convergence
    let converged = true;
    for (let i = 0; i < k; i++) {
      if (colorDistance(
        [centroids[i].r, centroids[i].g, centroids[i].b],
        [newCentroids[i].r, newCentroids[i].g, newCentroids[i].b]
      ) > 1) {
        converged = false;
        break;
      }
    }

    centroids = newCentroids;

    if (converged) break;
  }

  return clusters;
};

/**
 * Find the pixel closest to the cluster centroid for marker placement
 */
const findRepresentativePosition = (
  cluster: Cluster
): { x: number; y: number } => {
  if (cluster.pixels.length === 0) {
    return { x: 0, y: 0 };
  }

  let minDist = Infinity;
  let representative = cluster.pixels[0];

  for (const pixel of cluster.pixels) {
    const dist = colorDistance(
      [pixel.r, pixel.g, pixel.b],
      [cluster.centroid.r, cluster.centroid.g, cluster.centroid.b]
    );
    if (dist < minDist) {
      minDist = dist;
      representative = pixel;
    }
  }

  return { x: representative.x, y: representative.y };
};

/**
 * Extract dominant colors from an image using k-means clustering
 */
export const extractDominantColors = (
  image: HTMLImageElement,
  imageSize: { width: number; height: number },
  startId: number,
  numColors: number = 6
): { spots: ColorSpot[]; nextId: number } => {
  const canvas = document.createElement("canvas");
  canvas.width = imageSize.width;
  canvas.height = imageSize.height;
  const ctx = canvas.getContext("2d");

  if (!ctx) return { spots: [], nextId: startId };

  ctx.drawImage(image, 0, 0, imageSize.width, imageSize.height);

  // Sample pixels from image
  const sampledPixels = samplePixels(ctx, imageSize.width, imageSize.height);

  if (sampledPixels.length < numColors) {
    return { spots: [], nextId: startId };
  }

  // Run k-means clustering
  const clusters = kMeansClustering(sampledPixels, numColors);

  // Sort clusters by size (most dominant first)
  clusters.sort((a, b) => b.pixels.length - a.pixels.length);

  // Convert clusters to ColorSpots
  const spots: ColorSpot[] = clusters
    .filter(cluster => cluster.pixels.length > 0)
    .map((cluster, index) => {
      const position = findRepresentativePosition(cluster);
      const { r, g, b } = cluster.centroid;

      return {
        id: startId + index,
        x: position.x,
        y: position.y,
        color: `rgb(${r},${g},${b})`,
      };
    });

  return {
    spots,
    nextId: startId + spots.length,
  };
};
